import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { TenantAuthProvider } from "@/context/TenantAuthContext";
import { LandlordAuthProvider } from "@/context/LandlordAuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { TenantProtectedRoute } from "@/components/TenantProtectedRoute";
import { LandlordProtectedRoute } from "@/components/LandlordProtectedRoute";
import { Layout } from "@/components/Layout";

import Login from "@/pages/Login";
import Landing from "@/pages/Landing";
import Signup from "@/pages/Signup";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Rooms from "@/pages/Rooms";
import Vacancies from "@/pages/Vacancies";
import Tenants from "@/pages/Tenants";
import TenantAdminEdit from "@/pages/TenantAdminEdit";
import TenantMessages from "@/pages/TenantMessages";
import Contracts from "@/pages/Contracts";
import Invoices from "@/pages/Invoices";
import Payments from "@/pages/Payments";
import Arrears from "@/pages/Arrears";
import Repairs from "@/pages/Repairs";
import Vendors from "@/pages/Vendors";
import Finance from "@/pages/Finance";
import Loans from "@/pages/Loans";
import Investment from "@/pages/Investment";
import Documents from "@/pages/Documents";
import Notifications from "@/pages/Notifications";
import AIAssistant from "@/pages/AIAssistant";
import Reports from "@/pages/Reports";
import SettingsCompany from "@/pages/SettingsCompany";
import SettingsUsers from "@/pages/SettingsUsers";
import SettingsRoles from "@/pages/SettingsRoles";
import AuditLog from "@/pages/AuditLog";
import Emails from "@/pages/Emails";
import TenantLogin from "@/pages/TenantLogin";
import TenantPortal from "@/pages/TenantPortal";
import TenantAccess from "@/pages/TenantAccess";
import TenantActivate from "@/pages/TenantActivate";
import TenantLayout from "@/pages/TenantLayout";
import TenantDashboard from "@/pages/TenantDashboard";
import TenantProfile from "@/pages/TenantProfile";
import TenantInvoices from "@/pages/TenantInvoices";
import TenantRepairs from "@/pages/TenantRepairs";
import TenantDocuments from "@/pages/TenantDocuments";
import TenantContact from "@/pages/TenantContact";
import Billing from "@/pages/Billing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PlatformCompanies from "@/pages/PlatformCompanies";
import LandlordLogin from "@/pages/LandlordLogin";
import LandlordLayout from "@/pages/LandlordLayout";
import LandlordDashboard from "@/pages/LandlordDashboard";
import Landlords from "@/pages/Landlords";
import LandlordDetail from "@/pages/LandlordDetail";

function App() {
  return (
    <div className="App">
      <I18nProvider>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route element={<TenantAuthProvider><Outlet /></TenantAuthProvider>}>
              <Route path="/tenant/login" element={<TenantLogin />} />
              <Route path="/tenant/activate" element={<TenantActivate />} />
              <Route element={<TenantProtectedRoute />}>
                <Route element={<TenantLayout />}>
                  <Route path="/tenant" element={<TenantDashboard />} />
                  <Route path="/tenant/profile" element={<TenantProfile />} />
                  <Route path="/tenant/invoices" element={<TenantInvoices />} />
                  <Route path="/tenant/repairs" element={<TenantRepairs />} />
                  <Route path="/tenant/documents" element={<TenantDocuments />} />
                  <Route path="/tenant/contact" element={<TenantContact />} />
                  <Route path="/tenant/contracts" element={<TenantPortal />} />
                </Route>
              </Route>
            </Route>
            <Route element={<LandlordAuthProvider><Outlet /></LandlordAuthProvider>}>
              <Route path="/landlord/login" element={<LandlordLogin />} />
              <Route element={<LandlordProtectedRoute />}>
                <Route element={<LandlordLayout />}>
                  <Route path="/landlord" element={<LandlordDashboard />} />
                  <Route path="/landlord/profile" element={<TenantProfile />} />
                </Route>
              </Route>
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/properties" element={<Properties />} />
                <Route path="/properties/:id" element={<PropertyDetail />} />
                <Route path="/rooms" element={<Rooms />} />
                <Route path="/vacancies" element={<Vacancies />} />
                <Route path="/tenants" element={<Tenants />} />
                <Route path="/tenants/:id/edit" element={<TenantAdminEdit />} />
                <Route path="/tenants/:id/messages" element={<TenantMessages />} />
                <Route path="/landlords" element={<Landlords />} />
                <Route path="/landlords/:id" element={<LandlordDetail />} />
                <Route path="/tenant-access" element={<TenantAccess />} />
                <Route path="/contracts" element={<Contracts />} />
                <Route path="/invoices" element={<Invoices />} />
                <Route path="/payments" element={<Payments />} />
                <Route path="/arrears" element={<Arrears />} />
                <Route path="/repairs" element={<Repairs />} />
                <Route path="/vendors" element={<Vendors />} />
                <Route path="/finance" element={<Finance />} />
                <Route path="/loans" element={<Loans />} />
                <Route path="/investment" element={<Investment />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/ai" element={<AIAssistant />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/settings/company" element={<SettingsCompany />} />
                <Route path="/settings/users" element={<SettingsUsers />} />
                <Route path="/settings/roles" element={<SettingsRoles />} />
                <Route path="/settings/audit" element={<AuditLog />} />
                <Route path="/settings/emails" element={<Emails />} />
                <Route path="/settings/billing" element={<Billing />} />
                <Route path="/admin/companies" element={<PlatformCompanies />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/payment/cancel" element={<PaymentCancel />} />
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
